import React, { MouseEvent, useEffect, useRef } from 'react';
import useCart from '@base/cart/useCart';
import Iconfont from '@base/lib/Iconfont';
import { observer, useStore } from 'base/store';
import CartMenu from './CartMenu';
import { RouteEnum } from 'pro/config/routers';
import { LoadingOutlined } from '@ant-design/icons';
import { useStoreState } from 'base/store';
import styles from './index.module.less';
import HeaderBarTrigger from '@app/common/PageHeader/HeaderBarTrigger';
import HeaderBarDropdown from '@app/common/PageHeader/HeaderBarDropdown';
import { addEvent, removeEvent } from 'ava-util/dist/dom-event';

type HeaderCartProps = {
    disableMiniCart?: boolean;
}

type CartTriggerProps = {
    qty: number;
    loading?: boolean;
}

const CartTrigger: React.FC<CartTriggerProps> = props => {
    const { qty, loading = false } = props;
    return (
        <span>
            <em className={styles.count}>
                {loading ? <LoadingOutlined style={{ fontSize: 12 }}/> : qty}
            </em>
            <Iconfont className="icon-cart2"/>
        </span>
    );
};

const CartIconLink: React.FC = observer(() => {
    const {
        miniCartStore,
        userStore: {
            state: {
                cartNum
            }
        }
    } = useStore();

    useEffect(() => {
        miniCartStore.setTotalQty(cartNum);
    }, [cartNum]);

    return (
        <HeaderBarTrigger
            id="js-cart-icon"
            href={RouteEnum.CART}
            content={<CartTrigger qty={miniCartStore.state.totalQty}/>}
        />
    )
});

const CartDropdown: React.FC = observer(() => {
    const cart = useCart({ showBonusProdList: true, isMiniCart: true });
    const { totalQty, cartLoading, summaryLoading } = cart;
    const menuRef = useRef<HTMLDivElement>(null);

    const { miniCartStore } = useStore();
    miniCartStore.activeMinicart();
    const { state: { reloadCount, reloadByBonusListCount, visible } } = miniCartStore;

    const close = () => miniCartStore.setVisible(false);

    useEffect(() => {
        if (reloadCount > 0) {
            cart.loadCart();
        }
    }, [reloadCount]);

    useEffect(() => {
        if (reloadByBonusListCount > 0) {
            cart.loadCart({ fromListBonusItem: true });
        }
    }, [reloadByBonusListCount]);

    useEffect(() => {
        if (visible) {
            const handleClickClose = (e: MouseEvent) => {
                const menu = menuRef.current;
                if (!menu || !menu.contains(e.target as HTMLElement)) {
                    close();
                }
            }
            addEvent(window.document, 'click', handleClickClose);
            return () => {
                removeEvent(window.document, 'click', handleClickClose);
            };
        }
    }, [visible]);

    useEffect(() => {
        // debug
        // miniCartStore.setVisible(true);

        addEvent(window, 'resize', close);
        addEvent(window, 'scroll', close);
        return () => {
            removeEvent(window, 'resize', close);
            removeEvent(window, 'scroll', close);
        };
    }, []);

    return (
        <HeaderBarDropdown
            menu={<CartMenu cart={cart} visible={visible} ref={menuRef}/>}
            open={visible}
            // trigger={visible ? ['click'] : ['hover']}
            onOpenChange={(visible) => miniCartStore.setVisible(visible)}
            placement="bottomRight"
            alignRight
            triggerProps={{
                href: RouteEnum.CART,
                content: <CartTrigger qty={totalQty} loading={cartLoading || summaryLoading}/>
            }}
            getPopupContainer={() => document.getElementById('js-header-bar-right') as HTMLDivElement}
        />
    )
});

const HeaderCart: React.FC<HeaderCartProps> = props => {
    const { disableMiniCart = false } = props;
    const { ua: { isMobile } } = useStoreState('rootStore');

    return disableMiniCart || isMobile ? <CartIconLink/> : <CartDropdown/>;
};

export default HeaderCart;
